import { useEffect, useState } from "react";
import { Link, useNavigation } from "../app/navigation";
import { useWorkspace } from "../app/workspace";
import { PublicHeader } from "../components/PublicHeader";
import { recordPublicAcquisitionEvent, recordUniqueLandingView } from "../lib/api";

const closingSteps = [
  ["1", "수업표에서 출결을 기록합니다", "대부분은 한 번에 출석 처리하고, 결석·지각처럼 확인할 학생만 남깁니다."],
  ["2", "연결된 일을 한 자리에서 봅니다", "회차 차감 여부, 보강 후보, 보호자 안내 초안을 원인이 된 출결과 함께 보여줍니다."],
  ["3", "결정하고 오늘을 마칩니다", "발송과 돈 관련 결정은 원장이 확인합니다. 처리 뒤에는 남은 일과 되돌리기 기록이 남습니다."],
] as const;

const roleFacts = [
  ["원장", "오늘 마침·수납·강사 기록·설정을 관리합니다."],
  ["강사", "현재는 원장이 담당 수업 권한을 준비합니다. 실제 강사 계정 초대는 아직 제공하지 않습니다."],
  ["보호자", "가입 없이 목적이 제한된 링크로 안내를 확인합니다."],
] as const;

export function LandingPage() {
  const { beginSample, loading } = useWorkspace();
  const { navigate } = useNavigation();
  const [starting, setStarting] = useState(false);
  const [sampleError, setSampleError] = useState("");

  useEffect(() => { void recordUniqueLandingView(); }, []);

  function recordPrimaryStart() {
    void recordPublicAcquisitionEvent("primary_start_clicked").catch(() => undefined);
  }

  async function openSample() {
    setStarting(true);
    setSampleError("");
    try {
      await beginSample();
      navigate("/try/today");
    } catch {
      setSampleError("둘러보기를 열지 못했어요. 잠시 뒤 다시 시도해 주세요.");
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="public-page landing-v4">
      <PublicHeader />
      <main>
        <section className="proof-hero" aria-labelledby="landing-title">
          <div className="proof-hero__copy">
            <p className="proof-kicker">1개 지점 학원·공부방 원장용</p>
            <h1 id="landing-title">출결 뒤에 생기는 일을,<br />오늘 안에 끝냅니다.</h1>
            <p className="proof-hero__lead">결석 한 건이 생기면 회차, 보강, 보호자 안내, 수납 확인을 따로 옮겨 적지 않도록 한 흐름으로 묶습니다. 마지막 수업 뒤에는 남은 일만 확인하세요.</p>
            <div className="proof-hero__actions">
              <Link className="primary-action primary-action--hero" href="/start" onClick={recordPrimaryStart}>우리 학원으로 시작</Link>
              <button type="button" className="text-action" onClick={() => void openSample()} disabled={starting || loading}>{starting ? "샘플 하루 준비 중…" : "내 정보 없이 1분 둘러보기"}</button>
            </div>
            <p className="proof-boundary">카드 등록 없음 · 합성 학생 데이터 · 전체 내보내기 제공</p>
            {sampleError ? <p className="proof-error" role="alert">{sampleError}</p> : null}
          </div>

          <div className="today-proof" aria-label="출결에서 오늘 마침까지 이어지는 예시">
            <header><div><span>중1 수학 A</span><strong>오늘 수업</strong></div><p><strong>4</strong><span>/ 4명 기록</span></p></header>
            <div className="today-proof__student"><div><span>민서</span><p><strong>김민서</strong><small>중1 · 남은 8회</small></p></div><b>결석</b></div>
            <ol>
              <li><span>수강 횟수</span><strong>이번 회차 차감 안 함</strong></li>
              <li><span>보강</span><strong>가능 시간 확인 필요</strong></li>
              <li><span>보호자 안내</span><strong>초안만 준비 · 미발송</strong></li>
            </ol>
            <footer><p><span>오늘 남은 일</span><strong><s>3</s> 2</strong></p><span>보강 후보로 남기고 확인한 뒤</span></footer>
          </div>
        </section>

        <section className="proof-sequence" aria-labelledby="proof-sequence-title">
          <header><p className="proof-kicker">작동 방식</p><h2 id="proof-sequence-title">출결을 원인 기록으로 삼습니다.</h2><p>장부를 여러 개 만드는 대신, 한 번의 기록에서 다음 확인 항목을 이어 만듭니다.</p></header>
          <ol>{closingSteps.map(([number, title, detail]) => <li key={number}><span>{number}</span><div><h3>{title}</h3><p>{detail}</p></div></li>)}</ol>
        </section>

        <section className="operator-proof" aria-labelledby="operator-proof-title">
          <div><p className="proof-kicker">누가 어떻게 쓰나요</p><h2 id="operator-proof-title">원장은 전체를 닫고,<br />강사와 보호자는 필요한 범위만 봅니다.</h2><p>역할 이름만 나누지 않습니다. 로그인한 구성원에게는 허용된 수업만, 보호자에게는 한 가지 목적의 만료 링크만 엽니다.</p></div>
          <dl>{roleFacts.map(([role, fact]) => <div key={role}><dt>{role}</dt><dd>{fact}</dd></div>)}</dl>
          <p className="operator-proof__boundary">현재 외부 메시지·결제·상담 연결은 꺼져 있습니다. 화면에서 초안과 영향을 확인할 수 있지만 실제 발송·승인은 연결 전까지 실행하지 않습니다.</p>
        </section>

        <section className="move-proof" aria-labelledby="move-proof-title">
          <div><p className="proof-kicker">도입과 이탈 모두 가볍게</p><h2 id="move-proof-title">지금 쓰는 엑셀에서 시작하고,<br />필요하면 다시 가져가세요.</h2></div>
          <ol><li><strong>기기에서 먼저 확인</strong><span>학생 파일의 열과 오류 행을 서버 전송 전에 확인합니다.</span></li><li><strong>저장 전 변경 내역</strong><span>새로 만들 항목, 중복, 건너뛸 행을 나눠 보여줍니다.</span></li><li><strong>내보내기와 되돌리기</strong><span>운영 기록 전체를 내보내고 가져온 묶음을 취소할 수 있습니다.</span></li></ol>
        </section>

        <section className="proof-close" aria-labelledby="proof-close-title">
          <div><p className="proof-kicker">직접 확인</p><h2 id="proof-close-title">결석 한 건을 처리해 보세요.</h2><p>가입하지 않아도 출결에서 오늘 남은 일이 줄어드는 데까지 같은 제품 코드로 실행됩니다.</p></div>
          <button type="button" className="primary-action primary-action--hero" onClick={() => void openSample()} disabled={starting || loading}>{starting ? "샘플 하루 준비 중…" : "1분 둘러보기 시작"}</button>
        </section>
      </main>
      <footer className="public-footer">
        <span>수업마침 · MUNOPS</span>
        <nav aria-label="정책 메뉴"><Link href="/privacy">개인정보 처리방침</Link><Link href="/terms">이용약관</Link><Link href="/status">서비스 상태</Link><Link href="/support">도움</Link></nav>
        <small>공개 가입은 열려 있으며 결제·외부 메시지·상담 provider는 연결 전까지 안전하게 꺼져 있습니다.</small>
      </footer>
    </div>
  );
}
